/**
 * Export Jurisdiction and Classification U.S. EAR99, India Not Subject to Regulations
 */

import React, { useEffect, useRef, useState, type JSX } from 'react';
import './footer.css';
import { useTranslation } from '../common/Translation/translationProvider';
import { type Language } from '../../service/translationService';

interface FooterLinkItem {
  label: string;
  link?: string;
  onClick?: () => void;
}

interface PopupConfig {
  isPopOpen: boolean;
  toggle: (e: React.MouseEvent) => void;
}

interface FooterSection {
  label: string;
  value: FooterLinkItem[] | { link: string };
  popUp?: PopupConfig;
}

type PortalType = 'customer' | 'supplier' | 'ciam' |'default';
type FooterLinkMap = Record<PortalType, Record<string, FooterSection>>;

const Footer: React.FC = () => {
  const [openPopup, setOpenPopup] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t, switchLanguage } = useTranslation();

  const base = 'footer.';
  const params = new URLSearchParams(location.search);
  const portalParam = (params.get('portal')?.toLowerCase() ?? '') as PortalType;

  const envBase: string = portalParam === 'ciam' ? import.meta.env.VITE_HOST_CIAM : import.meta.env.VITE_HOST_PWC;

  // Toggle popup dynamically
  const togglePopup = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    setOpenPopup(openPopup === key ? null : key);
  };

  const handleSelect = (value: string) => {
    switchLanguage(value as Language);
    setOpenPopup(null);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpenPopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Type-safe footer map
  const footerLinkMap: FooterLinkMap = {
    customer: {
      privacy: {
        label: t(base+'privacy.label'),
        value: { link: t(base + 'privacy.link') },
      },
      tncOptions: {
        label: t(base + 'tnc.label'),
        value: { link: `${envBase}${t(base + 'tnc.pwc_customer.link')}` },
      },
      languageOptions: {
        label: t(base + 'language.label'),
        value: [
          { label: t(base + 'language.pwc_customer.english'), onClick: () => handleSelect('en-us') },
          { label:  t(base + 'language.pwc_customer.french'), onClick: () => handleSelect('fr-ca') },
          { label: t(base + 'language.pwc_customer.polish'), onClick: () => handleSelect('pl') },
        ],
      },
      helpdeskOptions: {
        label: t(base + 'helpdesk.label'),
        value: [
          { label: t(base + 'helpdesk.pwc_customer.email'),link:`mailto:${t(base + 'helpdesk.pwc_customer.email')}` },
          { label: t(base + 'helpdesk.pwc_customer.phone') },
        ],
      },
      userGuides: {
        label: t(base + 'userGuide.label'),
        value: { link: `${envBase}${t(base + 'userGuide.pwc_customer.link')}` },
      },
    },
    supplier: {
      privacy: {
        label: t(base+'privacy.label'),
        value: { link: t(base + 'privacy.link') },
      },
      tncOptions: {
        label: t(base + 'tnc.label'),
        value: [
          {
            label: t(base + 'tnc.pwc_supplier.canada.label'),
            link: `${envBase}${t(base + 'tnc.pwc_supplier.canada.link')}`,
          },
          {
            label: t(base + 'tnc.pwc_supplier.poland.label'),
            link: `${envBase}${t(base + 'tnc.pwc_supplier.poland.link')}`,
          },
        ],
      },
      languageOptions: {
        label: t(base + 'language.label'),
        value: [
          { label: t(base + 'language.pwc_supplier.english'), onClick: () => handleSelect('en-us') },
          { label:  t(base + 'language.pwc_supplier.french'), onClick: () => handleSelect('fr-ca') },
          { label: t(base + 'language.pwc_supplier.polish'), onClick: () => handleSelect('pl') },
        ],
      },
      helpdeskOptions: {
        label: t(base + 'helpdesk.label'),
        value: { link: `${envBase}${t(base + 'helpdesk.pwc_supplier.link')}` },
      },
      userGuides: {
        label: t(base + 'userGuide.label'),
        value: [
          {
            label: t(base + 'userGuide.pwc_supplier.canadaExternal.label'),
            link: `${envBase}${t(base + 'userGuide.pwc_supplier.canadaExternal.link')}`,
          },
          {
            label: t(base + 'userGuide.pwc_supplier.polandExternal.label'),
            link: `${envBase}${t(base + 'userGuide.pwc_supplier.polandExternal.link')}`,
          }
        ],
      },
    },
    ciam:{
      privacy: {
        label: t(base+'privacy.ciam.label'),
        value: { link: t(base + 'privacy.ciam.link') },
      },
      tncOptions: {
        label: t(base + 'tnc.ciam.label'),
        value: { link: t(base + 'tnc.ciam.link') },
      },
      helpdeskOptions: {
        label: t(base + 'helpdesk.ciam.label'),
         value: [
          { label: t(base + 'helpdesk.ciam.email'),link:`mailto:${t(base + 'helpdesk.ciam.email')}`  },
          { label: t(base + 'helpdesk.ciam.phone') },
        ],
      },
      userGuides: {
        label: t(base + 'userGuide.ciam.label'),
        value: { link: `${envBase}${t(base + 'userGuide.ciam.link')}` },
      },
    },
    default: {
      privacy: {
        label: t(base+'privacy.label'),
        value: { link: t(base + 'privacy.link') },
      },/* 
      tncOptions: {
        label: t(base + 'tnc.label'),
        value: { link: t(base + 'tnc.default.link') },
      }, */
     languageOptions: {
        label: t(base + 'language.label'),
        value: [
          { label: t(base + 'language.default.english'), onClick: () => handleSelect('en-us') },
          { label:  t(base + 'language.default.french'), onClick: () => handleSelect('fr-ca') },
          { label: t(base + 'language.default.polish'), onClick: () => handleSelect('pl') },
        ],
      },
      helpdeskOptions: {
        label: t(base + 'helpdesk.label'),
         value: [
          { label: t(base + 'helpdesk.default.email'),link:`mailto:${t(base + 'helpdesk.default.email')}`  },
          { label: t(base + 'helpdesk.default.phone') },
        ],
      },
    }
  };

  // Renders dropdown popup (languages, helpdesk, etc.)
  const renderPopup = (isOpen: boolean, items: FooterLinkItem[]): JSX.Element | null => {
    if (!isOpen || !items?.length) return null;
    return (
      <div ref={wrapperRef} className="popup-select-box above">
        <ul>
          {items.map((item, idx) => (
            <li key={idx}>
              {item.link ? (
                <a href={item.link} target="_blank" rel="noopener noreferrer">
                  {item.label}
                </a>
              ) : (
                <span onClick={item.onClick}>{item.label}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Dynamically builds the footer links
  const getFooterView = (): JSX.Element[] => {
    let links = footerLinkMap[portalParam]?? footerLinkMap.default;

    return Object.entries(links).map(([key, data]) => {
      const { label, value } = data;
      const isArray = Array.isArray(value);

      if (isArray) {
        return (
          <div className="popup-anchor" key={key}>
            {renderPopup(openPopup === key, value)}
            <a
              className="footer_links"
              href="#"
              onClick={(e) => togglePopup(e, key)}
            >
              {label}
            </a>
          </div>
        );
      }

      return (
        <a
          className="footer_links"
          key={key}
          href={value.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      );
    });
  };

  const isAnyPopupOpen = openPopup !== null;

  return (
    <footer className="footer">
      {isAnyPopupOpen && <div className="popup-overlay" />}
      <div className="footer-right">{getFooterView()}</div>
    </footer>
  );
};

export default Footer;
